import React, { ReactElement } from "react";
import {
    FormFieldReplacementInput,
    FormFieldType,
    WorkflowAutomaticFollowupExecuteTrigger, WorkflowAutomaticFollowUpStep,
    WorkflowAutomaticFollowupStepAction,
} from "../../../__generated__/api";
import { useFormBuilder } from "../../dirty-tracker/FormTracker";
import Select from "../../select/Select";
import { ActionProps } from "./base";

import createId from "../../../create-id";
import Editor from "../../agent-v3/editor/Editor";
import DurationPicker from "../../pickers/time/DurationPicker";
import UserGroups from "../../pickers/user-groups/UserGroups";
import Alert from "../../theme/alert/Alert";
import Badge from "../../theme/badge/Badge";
import Button from "../../theme/button/Button";
import Icon from "../../theme/icon/Icon";
import Styles from "./automatic-followup.module.scss";

const ACTION_ID = `urn:fq:workflow-service-action:(urn:fq:workflow-service:system,automatic-followup)`;

const TRIGGER_ACTION = "urn:fq:workflow-service-action:(urn:fq:workflow-service:system,automatic-followup)";
const FORM_FIELD_CONDITION = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,condition)";
const FORM_FIELD_MESSAGE = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,message)";
const FORM_FIELD_CONDITION_GROUP = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,condition-group)";
const FORM_FIELD_STEPS = "urn:fq:workflow-service-form-field:(urn:fq:workflow-service:system,steps)";

const FINISHED_CHILD_ACTION = `urn:fq:workflow-service-action-child:(${ACTION_ID},finished)`;

const CONDITIONS = [
    {
        label: "If no new messages from an the source channel",
        value: WorkflowAutomaticFollowupExecuteTrigger.NoUserMessage,
    },
    {
        label: "If no new messages from the agent in the ticketing system",
        value: WorkflowAutomaticFollowupExecuteTrigger.NoAgentMessage,
    },
    {
        label: "If no new message from a user in a specific set of groups",
        value: WorkflowAutomaticFollowupExecuteTrigger.NoMessageUserIncludeGroup,
    },
    {
        label: "If no new message from a user (excluding users in a group)",
        value: WorkflowAutomaticFollowupExecuteTrigger.NoMessageUserExcludeGroup,
    },
];

const STEP_OPTIONS = [
    {
        label: "Send a message",
        value: WorkflowAutomaticFollowupStepAction.SendMessage,
    },
    {
        label: "Take an action",
        value: WorkflowAutomaticFollowupStepAction.CustomStep,
    },
];

export default function AutomaticFollowupSettings(props: ActionProps): ReactElement {
    const { properties, tracker } = useFormBuilder(
        builder => builder
            .property(
                "condition",
                () => props.values.find(row => row.value.fieldId === FORM_FIELD_CONDITION)?.value.string,
            )
            .property(
                "conditionGroup",
                () => props.values.find(row => row.value.fieldId === FORM_FIELD_CONDITION_GROUP)?.value.options,
            )
            .array(
                "steps",
                () => JSON.parse(props.values.find(
                    row => row.value.fieldId === FORM_FIELD_STEPS,
                )?.value.string ?? "[]") as WorkflowAutomaticFollowUpStep[],
            ),
        [props.values],
    );

    tracker.useOnChange(() => {
        const allowedChildren = properties.steps.value.map((step, index) => (
            step.action === WorkflowAutomaticFollowupStepAction.CustomStep
                ? {
                    id: `urn:fq:workflow-service-action-child:(${ACTION_ID},${step.id})`,
                    label: `Step ${index + 1}`,
                    description: "What steps to take when this followup action is required",
                }
                : undefined)).filterNull();
        allowedChildren.push({
            id: FINISHED_CHILD_ACTION,
            label: `Finished`,
            description: "What action to take when all followups attempts have been depleted",
        });

        props.onChange({
            allowedChildren,
            values: [
                {
                    fieldId: FORM_FIELD_CONDITION,
                    type: FormFieldType.Text,
                    displayValue: properties.condition.value,
                    string: properties.condition.value,
                },
                {
                    fieldId: FORM_FIELD_CONDITION_GROUP,
                    type: FormFieldType.Text,
                    displayValue: "Groups",
                    options: properties.conditionGroup.value,
                },
                {
                    fieldId: FORM_FIELD_STEPS,
                    type: FormFieldType.Text,
                    displayValue: "Steps",
                    string: JSON.stringify(properties.steps.value),
                },
            ],
        });
    }, [props.values]);

    return (
        <div className="">
            <div>
                <div className="">
                    Condition
                </div>

                <div className="">
                    <Select
                        minRequired={1}
                        onChange={value => {
                            const isEmpty = properties.condition.value == null;
                            properties.condition.setValue(value.value);

                            if (isEmpty) {
                                properties.steps.setValue([
                                    ...properties.steps.value,
                                    { id: createId() } as WorkflowAutomaticFollowUpStep,
                                    { id: createId() } as WorkflowAutomaticFollowUpStep,
                                ]);
                            }
                        }}
                        value={properties.condition.value
                            ? CONDITIONS.find(row => row.value === properties.condition.value)
                            : undefined}
                        options={CONDITIONS}
                    />
                </div>
                <small>
                    In which case should the followup should continue.
                </small>
            </div>

            {(properties.condition.value === WorkflowAutomaticFollowupExecuteTrigger.NoMessageUserExcludeGroup
            || properties.condition.value === WorkflowAutomaticFollowupExecuteTrigger.NoMessageUserIncludeGroup) && (
                <div className="mt-3">
                    <div className="">
                        Groups
                    </div>

                    <div className="">
                        <UserGroups
                            isMulti
                            value={properties.conditionGroup.value as string[]}
                            onSelected={properties.conditionGroup.setValue}
                        />
                    </div>
                    <small>
                        {properties.condition.value === WorkflowAutomaticFollowupExecuteTrigger.NoMessageUserExcludeGroup
                            ? (
                                <>
                                    When a new message comes in from someone in these specific
                                    groups, the automatic follow-up should keep going.
                                </>
                            )
                            : (
                                <>
                                    If a new message isn&apos;t received from someone in these groups,
                                    then the automatic follow-up should continue.
                                </>
                            )}
                    </small>
                </div>
            )}

            <div className="mt-4">
                {properties.condition.value != null && (
                    <Button.Add
                        size="small"
                        variant="primary"
                        onClick={() => {
                            properties.steps.setValue([
                                ...properties.steps.value,
                            { id: createId() } as WorkflowAutomaticFollowUpStep,
                            ]);
                        }}
                    >
                        Add Step
                    </Button.Add>
                )}
                <div>
                    {properties.steps.value.map((step, index) => (
                        <div className={Styles.step}>
                            <div className={Styles["step-line"]}>
                                <div className={Styles.description}>
                                    <Badge size="medium" className={Styles.badge} variant="primary">{index + 1}</Badge>

                                    {" "}
                                    {CONDITIONS.find(row => row.value === properties.condition.value)?.label}
                                    {" "}
                                    in
                                </div>

                                <DurationPicker
                                    value={step.duration}
                                    className={Styles.duration}
                                    isRequired
                                    onChange={value => {
                                    // eslint-disable-next-line no-param-reassign
                                        step.duration = value?.time;
                                        properties.steps.setValue([...properties.steps.value]);
                                    }}
                                />
                                <div>
                                    then
                                </div>

                                <Select
                                    minRequired={1}
                                    onChange={value => {
                                    // eslint-disable-next-line no-param-reassign
                                        step.action = value?.value as WorkflowAutomaticFollowupStepAction;
                                        properties.steps.setValue([...properties.steps.value]);
                                    }}
                                    value={step.action
                                        ? STEP_OPTIONS.find(row => row.value === step.action)
                                        : undefined}
                                    options={STEP_OPTIONS}
                                />
                                <div className={Styles.splitter} />

                                <Button.Delete
                                    size="small"
                                    variant="none"
                                    className={Styles.delete}
                                    onClick={() => {
                                        properties.steps.setValue(properties.steps.value.filter(row => row.id !== step.id));
                                    }}
                                />

                            </div>
                            <div>
                                {step.action === WorkflowAutomaticFollowupStepAction.SendMessage && (
                                    <Editor
                                        value={step.message?.value?.string ?? ""}
                                        size="medium"
                                        hasBorder
                                        variables={{
                                            variables: props.input,
                                            shouldReplaceVariables: true,
                                            valueFormat: "auto",
                                            selectedVariables: step.message?.replacement as FormFieldReplacementInput[],
                                        }}
                                        onChange={(value, __clean, _mentions, replacements) => {
                                            // eslint-disable-next-line no-param-reassign
                                            step.message = {
                                                value: {
                                                    fieldId: FORM_FIELD_MESSAGE,
                                                    string: value ?? "",
                                                    type: FormFieldType.Text,
                                                },
                                                replacement: replacements,
                                            };
                                            // eslint-disable-next-line no-param-reassign
                                            properties.steps.setValue([...properties.steps.value]);
                                        }}
                                        placeholder="We have not heard from you..."
                                    />
                                )}
                                {step.action === WorkflowAutomaticFollowupStepAction.CustomStep && (
                                    <Alert variant="primary" prefixIcon={<Icon.Alert />}>
                                        After saving this automation step, you will be able to
                                        define the custom actions in the automation builder.
                                    </Alert>
                                )}
                            </div>

                        </div>
                    ))}
                </div>
            </div>

        </div>

    );
}
